import { useState } from 'react'
import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { YlyGrid } from './shared/YlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneYearData } from '../../hooks/useDlyOneYearData'
import { WIND_SCALE } from './shared/weatherUtils'

const SLUG = 'dly-one-year-wind'

const MODES = [
  { tsId: 'DLY_WSMAX', label: 'Max. rychlost', unit: 'm/s' },
  { tsId: 'DLY_WSAVG', label: 'Prům. rychlost', unit: 'm/s' },
]

function windCellStyle(value) {
  if (value == null) return null
  const entry = WIND_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function windFormat(value) {
  if (value == null) return '—'
  return value.toFixed(1)
}

function WindLegend() {
  return (
    <div className="shrink-0 flex flex-col gap-0.5">
      <div className="text-xs font-semibold text-gray-400 tracking-wide mb-1">m/s</div>
      {WIND_SCALE.map(({ label, bg }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="inline-block w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: bg }} />
          <span className="text-gray-600">{label}</span>
        </div>
      ))}
    </div>
  )
}

export function DlyOneYearWindView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const [modeIdx, setModeIdx] = useState(0)

  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const mode = MODES[modeIdx]
  const { dataMap, isLoading, isError, error } = useDlyOneYearData(station?.gh_id, year, mode.tsId)

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {MODES.map((m, i) => (
                <button
                  key={m.tsId}
                  onClick={() => setModeIdx(i)}
                  className={`px-3 py-1.5 transition-colors ${
                    i === modeIdx
                      ? 'bg-blue-600 text-white font-medium'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && <p className="text-red-500 text-sm">{error?.message ?? 'Chyba při načítání dat'}</p>}
        {!isLoading && !isError && (
          <div className="flex-1 min-h-0 flex gap-6">
            <div className="flex-1 min-w-0 min-h-0">
              <YlyGrid year={year} dataMap={dataMap} getCellStyle={windCellStyle} formatCell={windFormat} orientation={orientation} />
            </div>
            <WindLegend />
          </div>
        )}
      </div>
    </div>
  )
}
